import * as React from "react";
import { Check, ChevronsUpDown, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input, Label } from "@ndd/ui";

// Local port of legacy lib/v2/rd-name.ts normalizeRdName (search matching).
function normalizeRdName(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}

export interface RdPickerEntry {
  id: string;
  slug: string;
  name: string;
  supervisorId: string | null;
  supervisorName: string | null;
  sessionCount: number;
}

// NO-651: the picker only selects enrolled RDs. RDs are added by an admin in
// the RD registry, never created here by name.
export type RdComboBoxValue = { kind: "existing"; rdId: string };

interface RdComboBoxProps {
  rds: RdPickerEntry[];
  value: RdComboBoxValue | null;
  onChange: (value: RdComboBoxValue | null) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  canChooseSupervisor?: boolean;
  defaultSupervisorId?: string;
}

export const NO_RD_EMPTY_BODY =
  "Only enrolled RDs can receive transcripts. An admin adds RDs from the RD registry.";

const RD_INITIALS_COLORS = [
  "#EC4899",
  "#0284C7",
  "#166534",
  "#CA8A04",
  "#EA580C",
  "#7C3AED",
];

function rdColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;

  return RD_INITIALS_COLORS[Math.abs(h) % RD_INITIALS_COLORS.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function filterRds(rds: RdPickerEntry[], normalizedQuery: string): RdPickerEntry[] {
  if (!normalizedQuery) return rds;

  return rds.filter(
    (r) =>
      r.name.toLowerCase().includes(normalizedQuery) ||
      (r.supervisorName ?? "").toLowerCase().includes(normalizedQuery)
  );
}

export function rdDisabledReason(
  rd: RdPickerEntry,
  canChooseSupervisor: boolean,
  defaultSupervisorId: string | undefined
): string | null {
  if (rd.supervisorId === null) return "No supervisor assigned — ask admin";

  if (!canChooseSupervisor && rd.supervisorId !== defaultSupervisorId) {
    const supervisorLabel = rd.supervisorName ? ` under ${rd.supervisorName}` : "";

    return `Already assigned${supervisorLabel} — ask admin`;
  }

  return null;
}

/** Frame 4:355: the typed query gets a title; the body names the one way in. */
export function RdPickerEmpty({ query }: { query: string }): React.ReactElement {
  const typed = query.trim();

  return (
    <div className="px-3 py-4 text-center">
      {typed && (
        <p className="text-body font-medium text-foreground">No RD named “{typed}”</p>
      )}
      <p className="mt-0.5 text-label text-muted-foreground">{NO_RD_EMPTY_BODY}</p>
    </div>
  );
}

function useCloseOnOutsideClick(
  containerRef: React.RefObject<HTMLDivElement | null>,
  setOpen: (open: boolean) => void
): void {
  React.useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const container = containerRef.current;
      if (container && !container.contains(e.target as Node)) setOpen(false);
    }

    document.addEventListener("mousedown", onDocClick);

    return () => document.removeEventListener("mousedown", onDocClick);
  }, [containerRef, setOpen]);
}

interface RdOptionRowProps {
  rd: RdPickerEntry;
  reason: string | null;
  isActive: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
}

function RdOptionRow({
  rd,
  reason,
  isActive,
  isSelected,
  onSelect,
  onHover,
}: RdOptionRowProps) {
  const enabled = reason === null;

  return (
    <button
      type="button"
      onClick={() => enabled && onSelect()}
      onMouseEnter={() => enabled && onHover()}
      disabled={!enabled}
      className={cn(
        "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-left",
        "transition-colors",
        !enabled && "opacity-55 cursor-not-allowed",
        enabled && isActive && "bg-primary-soft",
        enabled && !isActive && "hover:bg-muted"
      )}
    >
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-micro font-semibold shrink-0"
        style={{
          background: enabled ? rdColor(rd.id) : "var(--color-sand-400)",
        }}
      >
        {initials(rd.name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-body text-foreground font-medium flex items-center gap-1.5 truncate">
          {rd.name}
          {!enabled && (
            <span className="inline-flex items-center gap-1 text-micro font-semibold uppercase tracking-wider text-warning bg-warning-soft px-1.5 py-[1px] rounded-full">
              <AlertTriangle className="h-3 w-3" />
              Unmapped
            </span>
          )}
        </div>
        <div className="text-caption text-muted-foreground truncate">
          {enabled ? (
            <>
              {rd.supervisorName} · {rd.sessionCount} session
              {rd.sessionCount === 1 ? "" : "s"}
            </>
          ) : (
            reason
          )}
        </div>
      </div>
      {isSelected && enabled && <Check className="h-4 w-4 text-primary shrink-0" />}
    </button>
  );
}

interface PickerRows {
  filtered: RdPickerEntry[];
  enabledRds: RdPickerEntry[];
  activeIndex: number;
  reasonOf: (rd: RdPickerEntry) => string | null;
}

function pickerRows(
  filtered: RdPickerEntry[],
  activeIndex: number,
  reasonOf: (rd: RdPickerEntry) => string | null
): PickerRows {
  const enabledRds = filtered.filter((rd) => reasonOf(rd) === null);
  const clamped = Math.max(0, Math.min(activeIndex, enabledRds.length - 1));

  return { filtered, enabledRds, activeIndex: clamped, reasonOf };
}

interface RdOptionListProps {
  query: string;
  rows: PickerRows;
  selectedRdId: string | null;
  onSelect: (rd: RdPickerEntry) => void;
  onHover: (index: number) => void;
}

function RdOptionList(props: RdOptionListProps) {
  const { query, selectedRdId } = props;
  const { filtered, enabledRds, activeIndex, reasonOf } = props.rows;

  return (
    <div className="absolute z-20 top-[calc(100%+4px)] left-0 right-0 max-h-[280px] overflow-y-auto rounded-[10px] border border-border bg-card shadow-[0_16px_32px_rgba(40,3,15,0.12)] p-1.5">
      {filtered.length === 0 && <RdPickerEmpty query={query} />}
      {filtered.map((rd) => {
        const enabledIndex = enabledRds.indexOf(rd);

        return (
          <RdOptionRow
            key={rd.id}
            rd={rd}
            reason={reasonOf(rd)}
            isActive={enabledIndex >= 0 && enabledIndex === activeIndex}
            isSelected={selectedRdId === rd.id}
            onSelect={() => props.onSelect(rd)}
            onHover={() => props.onHover(enabledIndex)}
          />
        );
      })}
    </div>
  );
}

function RdSearchField(props: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  query: string;
  onQueryChange: (value: string) => void;
  onFocus: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}) {
  return (
    <div className="relative">
      <Input
        id="rd-combobox-input"
        ref={props.inputRef}
        value={props.query}
        onChange={(e) => props.onQueryChange(e.target.value)}
        onFocus={props.onFocus}
        onKeyDown={props.onKeyDown}
        placeholder="Search RDs…"
        disabled={props.disabled}
        className="h-9 rounded-[10px] bg-background border-border text-foreground text-body pr-9 pl-3 placeholder:text-subtle shadow-none"
        autoComplete="off"
      />
      <ChevronsUpDown className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-subtle pointer-events-none" />
    </div>
  );
}

interface PickerKeys {
  open: boolean;
  setOpen: (open: boolean) => void;
  count: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  onEnter: () => void;
}

function pickerKeyHandler(keys: PickerKeys) {
  return (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!keys.open) {
      if (OPEN_KEYS.has(e.key)) {
        keys.setOpen(true);
        e.preventDefault();
      }

      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      keys.setActiveIndex((i) => Math.min(i + 1, Math.max(0, keys.count - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      keys.setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      keys.onEnter();
    } else if (e.key === "Escape") {
      keys.setOpen(false);
    }
  };
}

function usePickerPopover(autoFocus: boolean | undefined) {
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);
  useCloseOnOutsideClick(containerRef, setOpen);

  return { open, setOpen, inputRef, containerRef };
}

function RdFieldLabel() {
  return (
    <Label
      htmlFor="rd-combobox-input"
      className="text-caption font-semibold uppercase tracking-wider text-muted-foreground"
    >
      RD
    </Label>
  );
}

function SelectedRdLine({ label }: { label: string }) {
  if (!label) return null;

  return (
    <p className="text-caption text-success mt-0.5">
      Selected: <strong>{label}</strong>
    </p>
  );
}

const OPEN_KEYS = new Set(["ArrowDown", "ArrowUp", "Enter"]);

export function RdComboBox(props: RdComboBoxProps) {
  const { rds, value, onChange, defaultSupervisorId } = props;
  const canChooseSupervisor = props.canChooseSupervisor ?? true;
  const { open, setOpen, inputRef, containerRef } = usePickerPopover(props.autoFocus);
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);
  const rows = pickerRows(filterRds(rds, normalizeRdName(query)), activeIndex, (rd) =>
    rdDisabledReason(rd, canChooseSupervisor, defaultSupervisorId)
  );
  const selectedLabel = rds.find((r) => r.id === value?.rdId)?.name ?? "";

  function selectExisting(rd: RdPickerEntry | undefined) {
    if (!rd || rows.reasonOf(rd) !== null) return;

    onChange({ kind: "existing", rdId: rd.id });
    setQuery(rd.name);
    setOpen(false);
  }

  const onKeyDown = pickerKeyHandler({
    open,
    setOpen,
    count: rows.enabledRds.length,
    setActiveIndex,
    onEnter: () => selectExisting(rows.enabledRds[rows.activeIndex]),
  });

  function onQueryChange(next: string) {
    setQuery(next);
    setOpen(true);
    setActiveIndex(0);
    if (value && next !== selectedLabel) onChange(null);
  }

  return (
    <div className="flex flex-col gap-1">
      <RdFieldLabel />

      <div className="relative" ref={containerRef}>
        <RdSearchField
          inputRef={inputRef}
          query={query}
          onQueryChange={onQueryChange}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          disabled={props.disabled}
        />

        {open && (
          <RdOptionList
            query={query}
            rows={rows}
            selectedRdId={value ? value.rdId : null}
            onSelect={selectExisting}
            onHover={setActiveIndex}
          />
        )}
      </div>

      <SelectedRdLine label={selectedLabel} />
    </div>
  );
}
